import { toPng } from "html-to-image";

const MAX_BYTES = 1_500_000;

export interface CaptureOptions {
  /**
   * CSS selector for nodes to exclude from the snapshot. The instant-feedback
   * dialog passes its own data-attribute here so the dialog overlay doesn't
   * end up baked into the screenshot.
   */
  excludeSelector?: string;
}

/** Capture the current viewport as a base64 PNG (re-encoded to JPEG if too big). */
export async function capturePage(opts: CaptureOptions = {}): Promise<string> {
  const filter = opts.excludeSelector
    ? (node: HTMLElement) => !(node.matches?.(opts.excludeSelector!) ?? false)
    : undefined;
  const dataUrl = await toPng(document.body, {
    cacheBust: true,
    pixelRatio: 1,
    backgroundColor: "#ffffff",
    filter,
    // Google Fonts and other cross-origin stylesheets throw SecurityError
    // when html-to-image tries to read their cssRules to inline @font-face
    // declarations. The capture still works; the warning is noise.
    // `skipFonts: true` stops the inlining attempt so the console stays
    // clean — captured text falls back to the system font stack, which
    // matches what the user sees if Inter is installed locally.
    skipFonts: true,
  });
  if (dataUrl.length > MAX_BYTES) {
    return await reencodeJpeg(dataUrl, 0.7);
  }
  return dataUrl;
}

async function reencodeJpeg(dataUrl: string, quality: number): Promise<string> {
  const img = await loadImage(dataUrl);
  const canvas = document.createElement("canvas");
  canvas.width = img.naturalWidth;
  canvas.height = img.naturalHeight;
  const ctx = canvas.getContext("2d");
  if (!ctx) return dataUrl;
  ctx.drawImage(img, 0, 0);
  return canvas.toDataURL("image/jpeg", quality);
}

function loadImage(src: string): Promise<HTMLImageElement> {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.onload = () => resolve(img);
    img.onerror = reject;
    img.src = src;
  });
}

interface DisplayMediaConstraints {
  video: { displaySurface?: string; cursor?: string } | boolean;
  audio?: boolean;
}

/**
 * Capture a single frame of the user's shared screen via the browser's native
 * Screen Capture API. Returns a JPEG data URL; the capture stream is stopped
 * immediately after the snapshot.
 */
export async function captureScreenShare(): Promise<string> {
  if (typeof navigator === "undefined" || !navigator.mediaDevices) {
    throw new Error("Screen share unsupported on this browser");
  }
  const mediaDevices = navigator.mediaDevices as MediaDevices & {
    getDisplayMedia: (
      constraints: DisplayMediaConstraints,
    ) => Promise<MediaStream>;
  };
  if (!mediaDevices.getDisplayMedia) {
    throw new Error("Screen share unsupported on this browser");
  }
  const stream = await mediaDevices.getDisplayMedia({
    video: { displaySurface: "browser", cursor: "motion" },
    audio: false,
  });
  try {
    const video = document.createElement("video");
    video.srcObject = stream;
    video.muted = true;
    await video.play();
    await new Promise((r) => requestAnimationFrame(r));
    // Zero-width frames produce `data:,` which would attach as a broken
    // image. Happens on some headless browsers and when the user cancels
    // mid-handshake.
    if (!video.videoWidth || !video.videoHeight) {
      throw new Error("Screen share returned an empty frame");
    }
    const canvas = document.createElement("canvas");
    canvas.width = video.videoWidth;
    canvas.height = video.videoHeight;
    const ctx = canvas.getContext("2d");
    if (!ctx) throw new Error("Canvas 2D context unavailable");
    ctx.drawImage(video, 0, 0);
    return canvas.toDataURL("image/jpeg", 0.8);
  } finally {
    for (const t of stream.getTracks()) t.stop();
  }
}
